import React, { useCallback, useEffect } from 'react'
import { rem } from 'polished'
import ExperiencesPageCategoryTile from './ExperiencesPageCategoryTile'
import { fillArray, take } from 'lib/array/arrayUtils'
import { replaceExperienceQueryString } from 'lib/experiences/experienceUtils'

import { useAppDispatch } from 'hooks/reduxHooks'
import { fetchExperienceListFacets } from 'actions/ExperienceActions'
import ExperiencesPageCategoryTileLoadingSkeleton from './ExperiencesPageCategoryTileLoadingSkeleton'
import useMostPopulatedSearchExperienceCategories, { CategoryData } from '../hooks/useMostPopulatedSearchExperienceCategories'
import CardCarousel from 'components/Luxkit/Carousel/CardCarousel'
import TextButton from 'components/Luxkit/Button/TextButton'
import Group from 'components/utils/Group'
import LineMapIcon from 'components/Luxkit/Icons/line/LineMapIcon'
import useExperienceSearchUrl from 'hooks/Experiences/useExperienceSearchUrl'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'

const MAX_DISPLAYED_CATEGORIES = 6

interface Props {
  place: App.Place;
}

function ExperiencesPageCategoriesCarousel(props: Props) {
  const { place } = props
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchExperienceListFacets(place.id))
    // eslint-disable-next-line
  }, [place])

  const [primaryCategories, fetching] = useMostPopulatedSearchExperienceCategories(place)
  const viewAllCategoriesLink = useExperienceSearchUrl({ place })
  const viewAllMapCategoriesLink = useExperienceSearchUrl({ place, isMapUrl: true })

  const trackEvent = useTrackExperienceOfferEvent()

  const handleViewMapClick = useCallback(() => {
    trackEvent('VIEW_ON_MAP_CLICKED')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [])

  const handleViewAllClick = useCallback(() => {
    trackEvent('VIEW_ALL_CLICKED')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [])

  const getSearchUrl = (
    place: App.Place,
    categoryData: CategoryData,
  ) => {
    const query = replaceExperienceQueryString({
      destinationId: place.id,
      destinationName: place.name,
      categories: [categoryData.parent.id.toString()],
    })
    return `/search/experiences?${query}`
  }

  if (primaryCategories.length === 0 && !fetching) {
    return null
  }

  return <>
    <CardCarousel pageSize={6} width={rem(180)}>
      {fetching && fillArray(MAX_DISPLAYED_CATEGORIES).map((_, index) => <ExperiencesPageCategoryTileLoadingSkeleton key={index} />)}
      {primaryCategories.length > 0 && <>
        {take(primaryCategories, MAX_DISPLAYED_CATEGORIES).map(cat => <ExperiencesPageCategoryTile
        key={cat.parent.id}
        category={cat.parent}
        count={cat.count}
        to={getSearchUrl(place, cat)}
        childCategories={cat.children}
      />)}
      </>}
    </CardCarousel>
    <Group direction="horizontal" horizontalAlign="end">
      <TextButton
        kind="tertiary"
        outdent="right"
        to={viewAllMapCategoriesLink}
        startIcon={<LineMapIcon />}
        onClick={handleViewMapClick}
      >
        View on map
      </TextButton>
      <TextButton
        kind="tertiary"
        variant="dark"
        outdent="right"
        to={viewAllCategoriesLink}
        onClick={handleViewAllClick}
      >
        View all
      </TextButton>
    </Group>
  </>
}

export default React.memo(ExperiencesPageCategoriesCarousel)
